import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { CompanyFilters, JobFilters } from '../lib/api';
import { queries } from '../lib/api';

/**
 * Filtered lists are fetched, not filtered in the browser.
 *
 * The filters are part of the query key, so each combination is cached on its
 * own and a stale result cannot be shown under a different filter.
 * `keepPreviousData` holds the previous page in place while the next one loads,
 * which is what stops every keystroke in a search box from blanking the list.
 */
export const useDashboard = () => useQuery({ queryKey: ['dashboard'], queryFn: queries.dashboard });
// Fetched on its own rather than folded into the dashboard payload: it reports
// whether the *collectors* are running, so it must still answer when the
// registry projection is empty or slow, and a reader watching a stalled feed
// recover should be able to refetch this alone.
export const useCollection = () => useQuery({ queryKey: ['collection'], queryFn: queries.collection });
export const useCompanies = (filters: CompanyFilters) => useQuery({
  queryKey: ['companies', filters],
  queryFn: () => queries.companies(filters),
  placeholderData: keepPreviousData,
});
export const useCompany = (slug: string) => useQuery({ queryKey: ['company', slug], queryFn: () => queries.company(slug), enabled: Boolean(slug) });
export const useEvents = () => useQuery({ queryKey: ['events'], queryFn: queries.events });
export const useCorroborationQueue = (enabled: boolean) => useQuery({
  queryKey: ['events', 'needs-corroboration'],
  queryFn: queries.eventsNeedingCorroboration,
  enabled,
});
export const useJobs = (filters: JobFilters) => useQuery({
  queryKey: ['jobs', filters],
  queryFn: () => queries.jobs(filters),
  placeholderData: keepPreviousData,
});
export const useProfiles = () => useQuery({ queryKey: ['profiles'], queryFn: queries.profiles });
export const useJobFit = (profile: string, filters: JobFilters) => useQuery({
  queryKey: ['jobFit', profile, filters],
  queryFn: () => queries.jobFit(profile, filters),
  placeholderData: keepPreviousData,
  enabled: profile !== '',
});
export const useResearch = () => useQuery({ queryKey: ['research'], queryFn: queries.research });
export const useExclusions = (reasonCode: string[]) => useQuery({
  queryKey: ['exclusions', reasonCode],
  queryFn: () => queries.exclusions(reasonCode),
  placeholderData: keepPreviousData,
});
export const useTaxonomy = () => useQuery({ queryKey: ['taxonomy'], queryFn: queries.taxonomy, staleTime: Infinity });
